import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../icons/Icon';
import { useSarFieldInfoConfiguration } from '../useSarFieldInfoConfiguration';
import { type SarFieldInfoDefinition } from '../config/SarFieldInfoConfiguration';

const SarFieldInfoBubble: React.FC<SarFieldInfoBubbleProps> = ({ visible, position, definition, onClose }) => {
  const bubbleRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!visible) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (bubbleRef.current && !bubbleRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleScroll = (event: Event) => {
      if (bubbleRef.current && bubbleRef.current.contains(event.target as Node)) {
        return;
      }
      onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [onClose, visible]);

  if (!visible || !position) {
    return null;
  }

  return createPortal(
    <div className='fixed inset-0 z-[1200] pointer-events-none'>
      <div
        ref={bubbleRef}
        className='pointer-events-auto absolute z-[1201] w-[520px] max-w-[90vw] rounded-2xl border border-[#D0D5DD] bg-white shadow-xl'
        style={{ top: position.top, left: position.left }}
      >
        <div className='flex items-center justify-between border-b border-[#E4E7EC] px-5 py-3 rounded-t-2xl bg-[#F5F6FA]'>
          <span className='text-sm font-semibold text-[#1D2939]'>{definition.title}</span>
          <button
            type='button'
            aria-label='Kapat'
            className='flex h-5 w-5 items-center justify-center rounded-sm text-[#667085] hover:bg-[#E4E7EC] transition-all duration-200'
            onClick={(event) => {
              event.stopPropagation();
              onClose();
            }}
          >
            <Icon icon='close' />
          </button>
        </div>
        <div className='max-h-[420px] overflow-y-auto px-5 py-4 flex flex-col gap-y-3'>
          {definition.sections.map((section) => (
            <div key={section.id} className='rounded-xl bg-[#F9FAFB] p-3 flex flex-col gap-y-2 border border-[#EEF2FF]'>
              <div className='flex items-center gap-x-2'>
                <Icon icon={section.icon} size={14} className='text-[#475467]' />
                <span className='text-sm font-semibold text-[#1D2939]'>{section.title}</span>
                {section.badge ? (() => {
                  let badgeClassName: string;
                  switch (section.badge.tone) {
                    case 'danger':
                      badgeClassName = 'bg-[#FFF1F3] text-[#D92D20]';
                      break;
                    case 'success':
                      badgeClassName = 'bg-[#ECFDF3] text-[#027A48]';
                      break;
                    case 'warning':
                    default:
                      badgeClassName = 'bg-orange-100 text-orange-400';
                      break;
                  }
                  return (
                    <span className={`ml-auto rounded-full px-2 py-[2px] text-[11px] font-semibold ${badgeClassName}`}>
                      {section.badge.text}
                    </span>
                  );
                })() : null}
              </div>
              <span className='text-sm leading-6 text-[#475467] whitespace-pre-line'>{section.description}</span>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
};

const useSarFieldInfoBubble = ({ fieldPath, fieldLabel, fieldType, additionalInfo }: SarFieldInfoBubbleHookParams): SarFieldInfoBubbleHookResult => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [position, setPosition] = useState<SarFieldInfoBubblePosition | null>(null);
  const definition = useSarFieldInfoConfiguration({ fieldPath, fieldLabel, fieldType, additionalInfo });

  const closeBubble = useCallback(() => {
    setIsVisible(false);
  }, []);

  const openBubble = useCallback((anchorRect: DOMRect) => {
    const desiredLeft = anchorRect.left + window.scrollX - 60;
    const width = Math.min(520, window.innerWidth - 32);
    const maxLeft = window.innerWidth - width - 16;
    const calculatedLeft = Math.max(16, Math.min(desiredLeft, maxLeft));
    const calculatedTop = anchorRect.bottom + window.scrollY + 8;
    setPosition({ top: calculatedTop, left: calculatedLeft });
    setIsVisible(true);
  }, []);

  const bubbleElement = useMemo(() => {
    if (!isVisible || !position) {
      return null;
    }
    return <SarFieldInfoBubble visible={isVisible} position={position} definition={definition} onClose={closeBubble} />;
  }, [closeBubble, definition, isVisible, position]);

  return {
    isVisible,
    openBubble,
    closeBubble,
    bubbleElement,
  };
};

const SarFieldInfoBubbleButton: React.FC<SarFieldInfoBubbleButtonProps> = ({ fieldPath, fieldLabel, fieldType, additionalInfo, trigger }) => {
  const { isVisible, openBubble, closeBubble, bubbleElement } = useSarFieldInfoBubble({ fieldPath, fieldLabel, fieldType, additionalInfo });
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const handleMouseDown = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  }, []);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      event.stopPropagation();
      event.preventDefault();
      const rect = event.currentTarget.getBoundingClientRect();
      if (isVisible) {
        closeBubble();
        requestAnimationFrame(() => openBubble(rect));
      } else {
        openBubble(rect);
      }
    },
    [closeBubble, isVisible, openBubble],
  );

  return (
    <>
      <button
        type='button'
        ref={buttonRef}
        className='p-0 w-3 m-0 border-none bg-transparent hover:cursor-pointer'
        onMouseDown={handleMouseDown}
        onClick={handleClick}
      >
        {trigger}
      </button>
      {bubbleElement}
    </>
  );
};

export interface SarFieldInfoBubblePosition {
  readonly top: number;
  readonly left: number;
}

export interface SarFieldInfoBubbleProps {
  readonly visible: boolean;
  readonly position: SarFieldInfoBubblePosition | null;
  readonly definition: SarFieldInfoDefinition;
  readonly onClose: () => void;
}

export interface SarFieldInfoBubbleHookParams {
  readonly fieldPath: string;
  readonly fieldLabel: string;
  readonly fieldType: string;
  readonly additionalInfo?: string;
}

export interface SarFieldInfoBubbleButtonProps extends SarFieldInfoBubbleHookParams {
  readonly trigger?: React.ReactElement;
}

interface SarFieldInfoBubbleHookResult {
  readonly isVisible: boolean;
  readonly openBubble: (anchorRect: DOMRect) => void;
  readonly closeBubble: () => void;
  readonly bubbleElement: React.ReactElement | null;
}

export default SarFieldInfoBubbleButton;
